// Local backup for "La Multipla del Ronin" history.
//
// Why this exists:
//   In some preview/deployment environments the server-side MongoDB volume
//   may be reset between sessions, wiping the user's saved slips. To prevent
//   data loss we mirror every save into ``localStorage`` and, on each
//   history load, ask the server to rehydrate any slip the user has locally
//   but the server has lost. The browser becomes the source of truth for
//   "did I ever save this slip?".
//
// IMPORTANT — owner scoping (April 2026 fix):
//   `db.ronin_saved` is a multi-tenant collection. Without scoping, user B's
//   localStorage could resurrect user A's deleted slips by re-POSTing them
//   to /rehydrate. Every slip is now tagged with ``owner_code`` (the access
//   code that saved it), and ``loadLocal`` filters by the currently-active
//   owner so only the user's own slips are ever sent for rehydration.

const STORAGE_KEY = "betcracker_ronin_slips_v1";

function _read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch {
    return {};
  }
}

function _write(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // QuotaExceeded or disabled storage — silent fallback.
  }
}

function _norm(code) {
  return (code || "").toString().trim().toUpperCase();
}

export function saveLocal(slip, ownerCode) {
  if (!slip || !slip.id) return;
  const map = _read();
  map[slip.id] = { ...slip, owner_code: _norm(ownerCode || slip.owner_code) };
  _write(map);
}

export function removeLocal(id) {
  if (!id) return;
  const map = _read();
  if (id in map) {
    delete map[id];
    _write(map);
  }
}

/**
 * Returns ALL slips ever saved on this device, regardless of owner. Useful
 * only for diagnostics — UI code should always call ``loadLocalForOwner``.
 */
export function loadLocal() {
  return Object.values(_read());
}

/**
 * Returns only the slips that were saved by ``ownerCode`` on this device.
 * If ``ownerCode`` is missing/empty, returns an empty array — we never
 * leak slips of another user.
 */
export function loadLocalForOwner(ownerCode) {
  const owner = _norm(ownerCode);
  if (!owner) return [];
  return Object.values(_read()).filter((s) => _norm(s.owner_code) === owner);
}

/**
 * Compare the server's slip list to the local one (scoped to ownerCode)
 * and return slips that exist locally for THIS user but are missing on
 * the server (i.e. they need to be rehydrated).
 */
export function findMissingOnServer(serverSlips, ownerCode) {
  const serverIds = new Set((serverSlips || []).map((s) => s.id).filter(Boolean));
  return loadLocalForOwner(ownerCode).filter((s) => s.id && !serverIds.has(s.id));
}

/**
 * Sync local mirror with the authoritative server list — drops local
 * entries the server has explicitly deleted (so a delete from another
 * device doesn't keep coming back).
 *
 * Only touches entries owned by ``ownerCode``: slips belonging to other
 * users on the same device (e.g. a shared browser) are left alone.
 *
 * NOTE: only call this AFTER any pending rehydrates have happened, otherwise
 * we'd wipe the very entries we're trying to restore.
 */
export function reconcileLocalWithServer(serverSlips, ownerCode) {
  const owner = _norm(ownerCode);
  const map = _read();
  // Remove this user's existing entries; keep other users' entries intact.
  for (const id of Object.keys(map)) {
    if (_norm(map[id]?.owner_code) === owner) delete map[id];
  }
  // Re-insert what the server says is the truth.
  for (const s of serverSlips || []) {
    if (s && s.id) map[s.id] = { ...s, owner_code: owner };
  }
  _write(map);
}
